<!-- ═══════════════════════════════════════════════════════════
     PROFILE UNAVAILABLE (e.g. this account has blocked you)
════════════════════════════════════════════════════════════ -->
<div *ngIf="profileUnavailable"
  class="w-full overflow-hidden rounded-2xl border border-[#e9e9e9] bg-white shadow-sm px-6 py-10 text-center">
  <div class="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
    <svg class="h-7 w-7 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  </div>
  <h3 class="text-base font-semibold text-gray-800">Profile unavailable</h3>
  <p class="mt-1 text-sm text-gray-500">This profile isn't available right now.</p>
</div>

<!-- ═══════════════════════════════════════════════════════════
     FRIEND REQUEST NOTIFICATION CARD
════════════════════════════════════════════════════════════ -->
<div *ngIf="!profileUnavailable && userInfo?.friendship?.status === 'PENDING_RECEIVED'"
  class="mb-4 w-full overflow-hidden rounded-2xl border border-[#e9e9e9] bg-white shadow-sm px-5 py-4 flex items-center gap-4">
  <img [src]="userInfo?.profileImage?.urlMedium || '/images/user/user-default.svg'" [alt]="userInfo?.userName"
    class="h-12 w-12 rounded-full object-cover flex-shrink-0 border border-[#e9e9e9]" />
  <div class="flex flex-1 flex-col gap-2 min-w-0">
    <p class="text-sm text-gray-700 leading-snug">
      <span class="font-semibold text-gray-900">{{ userInfo?.userName }}</span>
      wants to be friends with you.
    </p>
    <div class="flex items-center gap-2">
      <button type="button" (click)="acceptFriendRequest($event)"
        class="flex-1 rounded-lg bg-brand-500 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-600 active:scale-95">
        Accept
      </button>
      <button type="button" (click)="rejectFriendRequest($event)"
        class="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 active:scale-95">
        Reject
      </button>
    </div>
  </div>
</div>


<!-- ═══════════════════════════════════════════════════════════
     MAIN PROFILE SIDEBAR CARD
════════════════════════════════════════════════════════════ -->
<aside *ngIf="!profileUnavailable" class="w-full overflow-hidden rounded-2xl border border-[#e9e9e9] bg-white shadow-sm lg:sticky lg:top-24">
  <div class="relative h-36 w-full sm:h-40" [ngStyle]="{
       'background-image': userInfo?.coverImage?.urlMedium
         ? 'url(' + userInfo.coverImage.urlMedium + ')'
         : '',
       'background-size': 'cover',
       'background-position': 'center'
     }" [ngClass]="{
       'bg-gradient-to-br from-rose-100 via-pink-50 to-white': !userInfo?.coverImage?.url
     }">
    <button type="button" (click)="openCoverImageModal()" *ngIf="userInfo?.isMyProfile"
      class="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-[#e9e9e9] bg-white text-gray-500"
      aria-label="Edit cover">
      <svg viewBox="0 0 24 24" class="h-4 w-4 fill-none stroke-current stroke-2">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
      </svg>
    </button>
    <div class="absolute -bottom-16 left-1/2 -translate-x-1/2 text-center">
      <div class="relative mx-auto h-28 w-28 rounded-full border-4 border-white bg-white shadow-md sm:h-32 sm:w-32">
        <img [src]="userInfo?.profileImage?.urlMedium || '/images/user/user-default.svg'" alt="Profile"
          class="h-full w-full rounded-full object-cover" />
        <button type="button" (click)="openProfileImageModal()" *ngIf="userInfo?.isMyProfile"
          class="absolute bottom-1 right-1 flex h-7 w-7 items-center justify-center rounded-full border border-[#e9e9e9] bg-white text-gray-600"
          aria-label="Edit profile photo">
          <svg viewBox="0 0 24 24" class="h-3.5 w-3.5 fill-none stroke-current stroke-2">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
          </svg>
        </button>
      </div>
    </div>
  </div>

  <div class="px-5 pb-5 pt-20 text-center sm:px-6">

    <!-- ── Blocked-by-me banner — replaces the normal action row ── -->
    <div *ngIf="userInfo?.isBlockedByMe" class="mb-5 flex flex-col items-center gap-2">
      <span class="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-500">
        <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
        </svg>
        You've blocked this account
      </span>
      <button type="button" (click)="unblockUser($event)"
        class="rounded-lg border border-[#D31148] bg-white px-4 py-1.5 text-xs font-semibold text-[#D31148] transition hover:bg-[#FFD4E0]">
        Unblock
      </button>
    </div>

    <div class="mb-5 flex items-center justify-center gap-3" *ngIf="!userInfo?.isBlockedByMe">
      <button type="button" (click)="redirectToSetting()" *ngIf="userInfo?.isMyProfile"
        class="rounded-lg border border-[#d8d8d8] bg-[#f8f8f8] px-3 py-1.5 text-xs font-semibold text-gray-700">
        Edit Profile Info
      </button>
      <!-- <button type="button" *ngIf="userInfo?.isMyProfile"
        class="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white">Dashboard</button> -->
      <button *ngIf="!userInfo?.isMyProfile && !userInfo?.isFollowed" (click)="follow($event)"
        class="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 border border-green-600 rounded-lg transition flex-shrink-0 hover:bg-green-200">
        Follow
      </button>
      <button *ngIf="!userInfo?.isMyProfile && userInfo?.isFollowed" (click)="unfollow($event)"
        class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-[#f8f8f8] border border-[#d8d8d8] hover:bg-gray-100 rounded-lg transition flex-shrink-0">
        Unfollow
      </button>

      <!-- Message button — shown to any logged-in visitor on someone else's profile -->
      <button *ngIf="!userInfo?.isMyProfile" (click)="openMessageModal()"
        title="Send message"
        class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-700 bg-brand-50 border border-brand-300 rounded-lg transition flex-shrink-0 hover:bg-brand-100 active:scale-95">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        Message
      </button>
      <button *ngIf="!userInfo?.isMyProfile" (click)="onFriendButtonClick($event)"
        [disabled]="userInfo?.friendship?.status === 'BLOCKED'"
        [title]="friendButtonTitle(userInfo?.friendship?.status)"
        class="text-gray-400 flex-shrink-0 transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:text-[#D31148]">
        <svg *ngIf="userInfo?.friendship?.status === 'NONE'" class="h-6 w-6" viewBox="0 0 24 24" fill="none"
          stroke="#D31148" stroke-width="2">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <line x1="19" y1="8" x2="19" y2="14" />
          <line x1="22" y1="11" x2="16" y2="11" />
        </svg>
        <svg *ngIf="userInfo?.friendship?.status === 'PENDING_SENT'" class="h-6 w-6" viewBox="0 0 24 24" fill="none"
          stroke="#f97316" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <line x1="17" y1="8" x2="23" y2="14" />
          <line x1="23" y1="8" x2="17" y2="14" />
        </svg>
        <svg *ngIf="userInfo?.friendship?.status === 'PENDING_RECEIVED'" class="h-6 w-6" viewBox="0 0 24 24" fill="none"
          stroke="#6366f1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <polyline points="16 11 18 13 22 9" />
        </svg>
        <svg *ngIf="userInfo?.friendship?.status === 'FRIENDS'" class="h-6 w-6" viewBox="0 0 24 24" fill="none"
          stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <polyline points="16 11 18 13 22 9" />
        </svg>
        <svg *ngIf="userInfo?.friendship?.status === 'BLOCKED'" class="h-6 w-6" viewBox="0 0 24 24" fill="none"
          stroke="#9ca3af" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
        </svg>
      </button>

      <!-- More options ("•••") — Block User -->
      <div class="relative" *ngIf="!userInfo?.isMyProfile">
        <button type="button" (click)="toggleMoreMenu($event)" title="More options"
          class="dropdown-toggle flex h-8 w-8 items-center justify-center rounded-lg border border-[#d8d8d8] text-gray-500 transition hover:bg-[#f8f8f8] flex-shrink-0">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
          </svg>
        </button>
        <app-dropdown [isOpen]="isMoreMenuOpen" (close)="closeMoreMenu()" className="w-44 py-1">
          <app-dropdown-item (click)="promptBlockUser()"
            baseClassName="block w-full text-left px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
            Block User
          </app-dropdown-item>
        </app-dropdown>
      </div>
    </div>

    <h2 class="text-2xl font-bold text-brand-500">{{ userInfo?.userName }}</h2>
    <p class="text-sm text-gray-600">&#64;{{ userInfo?.displayName }}</p>

    <ng-container *ngIf="!userInfo?.isBlockedByMe">
      <div class="mt-5 space-y-1 text-left text-sm text-gray-700">
        <p><span class="font-medium text-gray-900" *ngIf="userInfo?.gender">Gender:</span> {{ userInfo?.gender }}</p>
        <p *ngIf="userInfo?.attractedTo?.length"><span class="font-medium text-gray-900">Attracted To:</span> {{
          attractedToDisplay }}</p>
        <p><span class="font-medium text-gray-900" *ngIf="userInfo?.status">Status:   </span>
          <span class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold align-middle"
            [ngClass]="{
              'bg-green-100 text-green-700': userInfo?.status === 'ACTIVE',
              'bg-amber-100 text-amber-700': userInfo?.status === 'ON HOLIDAY'
            }">
            <span class="h-1.5 w-1.5 rounded-full"
              [ngClass]="{
                'bg-green-500': userInfo?.status === 'ACTIVE',
                'bg-amber-500': userInfo?.status === 'ON HOLIDAY'
              }"></span>
            {{ userInfo?.status }}
          </span>
        </p>
      </div>
      <p class="mt-5 text-left text-sm leading-6 text-gray-600 whitespace-pre-wrap break-words">{{ userInfo?.bio }}</p>
      <div *ngIf="visiblePreferences.length" class="mt-5 space-y-3 border-t border-[#efefef] pt-5 text-left">
        <div *ngFor="let pref of visiblePreferences">
          <p class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">{{ pref.label }}</p>
          <div class="flex flex-wrap gap-1.5">
            <span *ngFor="let val of pref.values"
              class="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-600">{{ val }}</span>
          </div>
        </div>
      </div>
      <div class="mt-6 grid grid-cols-2 gap-4 border-t border-[#efefef] pt-5 sm:grid-cols-4">
        <div class="text-center">
          <p class="text-lg font-semibold text-gray-900">{{ userInfo?.followers }}</p>
          <p class="text-xs font-medium text-gray-500">Followers</p>
        </div>
        <div class="text-center">
          <p class="text-lg font-semibold text-gray-900">{{ userInfo?.following }}</p>
          <p class="text-xs font-medium text-gray-500">Following</p>
        </div>
        <div class="text-center">
          <p class="text-lg font-semibold text-gray-900">{{ userInfo?.reactions }}</p>
          <p class="text-xs font-medium text-gray-500">Reactions</p>
        </div>
        <div class="text-center">
          <p class="text-lg font-semibold text-gray-900">{{ userInfo?.comments }}</p>
          <p class="text-xs font-medium text-gray-500">Comments</p>
        </div>
      </div>
    </ng-container>
  </div>
</aside>


<!-- ═══════════════════════════════════════════════════════════
     CAMERA OVERLAY — profile image only
════════════════════════════════════════════════════════════ -->
<app-camera-capture [isOpen]="isCameraOpen" (photoTaken)="onProfilePhotoCaptured($event)"
  (cancelled)="closeProfileCamera()">
</app-camera-capture>


<!-- ═══════════════════════════════════════════════════════════
     SEND MESSAGE MODAL
     Instagram-style: type a message → creates DM / request → go to chat
════════════════════════════════════════════════════════════ -->
<app-modal [isOpen]="isMessageModalOpen" (close)="closeMessageModal()" className="max-w-md">
  <div class="flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900">

    <!-- Header -->
    <div class="flex items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-800">
      <div class="flex items-center gap-3">
        <img [src]="userInfo?.profileImage?.urlSmall || '/images/user/user-default.svg'"
             [alt]="userInfo?.displayName"
             class="h-9 w-9 rounded-full object-cover border border-gray-200">
        <div>
          <p class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ userInfo?.displayName }}</p>
          <p class="text-xs text-gray-400 dark:text-gray-500">&#64;{{ userInfo?.userName }}</p>
        </div>
      </div>
      <button (click)="closeMessageModal()"
              class="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 transition-colors">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    </div>

    <!-- Message input area -->
    <div class="px-5 py-4">
      <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
        Write your message below. It will be sent as a
        <span *ngIf="userInfo?.friendship?.status !== 'FRIENDS'" class="font-medium text-amber-600">message request</span>
        <span *ngIf="userInfo?.friendship?.status === 'FRIENDS'" class="font-medium text-brand-600">direct message</span>
        to {{ userInfo?.displayName }}.
      </p>

      <textarea
        [(ngModel)]="messageText"
        (keydown)="onMessageKeyDown($event)"
        placeholder="Write a message…"
        rows="4"
        [disabled]="isSendingMessage"
        class="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white/90 dark:placeholder:text-white/30 disabled:opacity-50">
      </textarea>

      <!-- Error -->
      <p *ngIf="messageSendError" class="mt-2 text-xs text-red-500">{{ messageSendError }}</p>
    </div>

    <!-- Footer -->
    <div class="flex items-center justify-end gap-3 border-t border-gray-100 px-5 py-4 dark:border-gray-800">
      <button (click)="closeMessageModal()"
              [disabled]="isSendingMessage"
              class="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 disabled:opacity-40">
        Cancel
      </button>
      <button (click)="sendFirstMessage()"
              [disabled]="!messageText.trim() || isSendingMessage"
              class="flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-40">
        <svg *ngIf="isSendingMessage" class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
        </svg>
        <svg *ngIf="!isSendingMessage" width="15" height="15" viewBox="0 0 20 20" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M4.98481 2.44399C3.11333 1.57147 1.15325 3.46979 1.96543 5.36824L3.82086 9.70527C3.90146 9.89367 3.90146 10.1069 3.82086 10.2953L1.96543 14.6323C1.15326 16.5307 3.11332 18.4291 4.98481 17.5565L16.8184 12.0395C18.5508 11.2319 18.5508 8.76865 16.8184 7.961L4.98481 2.44399Z" fill="white"/>
        </svg>
        {{ isSendingMessage ? 'Sending…' : 'Send Message' }}
      </button>
    </div>
  </div>
</app-modal>


<!-- ═══════════════════════════════════════════════════════════
     PROFILE IMAGE MODAL
════════════════════════════════════════════════════════════ -->
<app-modal [isOpen]="isProfileImageOpen" (close)="closeProfileImageModal()" className="max-w-[700px]">
  <div class="relative w-full h-full flex flex-col bg-white dark:bg-gray-900 rounded-3xl overflow-hidden">

    <div class="flex-shrink-0 px-6 pt-6 pb-4 bg-white dark:bg-gray-900 border-b border-gray-200">
      <h4 class="text-2xl font-semibold text-gray-800 dark:text-white/90">Profile Image</h4>
    </div>

    <div class="flex-1 overflow-y-auto px-6 py-5">

      <!-- ── Upload zone (no image chosen yet) ──────────────── -->
      <div *ngIf="!selectedImageUrl">
        <div
          class="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 p-10 text-center transition-colors dark:border-gray-700 dark:bg-gray-800"
          [class.border-rose-500]="isDragging" [class.bg-rose-50]="isDragging" (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">

          <!-- Icon -->
          <div class="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
            <svg class="h-7 w-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </div>

          <div>
            <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Select a file or drag and drop here</p>
            <p class="mt-1 text-xs text-gray-400">JPG, PNG, JPEG, HEIC — max size 20MB</p>
          </div>

          <!-- ── Two action buttons ── -->
          <div class="grid grid-cols-1 gap-3 mt-1 min-[500px]:flex min-[500px]:items-center">
            <!-- Select from device -->
            <button type="button" (click)="fileInput.click()" style="display: flex; justify-content: center;"
              class="flex items-center gap-2 rounded-lg bg-rose-600 px-5 py-2 text-sm font-semibold uppercase tracking-wider text-white transition hover:bg-rose-700 active:scale-95">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Select File
            </button>

            <!-- Capture live photo -->
            <button type="button" (click)="openProfileCamera()"
              class="flex items-center gap-2 rounded-lg border-2 border-rose-600 text-rose-600 px-5 py-2 text-sm font-semibold uppercase tracking-wider bg-white hover:bg-rose-50 active:scale-95 transition">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <circle cx="12" cy="13" r="4" stroke="currentColor" stroke-width="2" />
              </svg>
              Take Photo
            </button>
          </div>

          <input #fileInput type="file" accept="image/jpg,image/jpeg,image/png,.jfif,image/heic,image/heif,.heic,.heif" class="hidden"
            (change)="onFileSelected($event)" />
        </div>
        <p *ngIf="fileError" class="mt-2 text-center text-sm text-red-500">{{ fileError }}</p>
      </div>

      <!-- ── Crop / adjust editor (image chosen) ────────────── -->
      <div *ngIf="selectedImageUrl">
        <div class="mb-3 flex items-center justify-between">
          <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Adjust &amp; Crop</span>
          <button type="button" (click)="resetImage()" class="text-xs text-rose-500 hover:underline">
            Remove &amp; choose again
          </button>
        </div>
        <div class="relative flex items-center justify-center overflow-hidden rounded-2xl bg-gray-100 dark:bg-gray-800"
          style="height: 280px;">
          <img #cropImage [src]="selectedImageUrl"
            [style.transform]="'translate(' + offsetX + 'px,' + offsetY + 'px) scale(' + zoomLevel + ')'"
            style="max-width: 25%; cursor: grab; transition: transform 0.05s linear; user-select: none;"
            draggable="false" (mousedown)="startDrag($event)" (touchstart)="startDragTouch($event)" />
          <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div class="h-48 w-48 rounded-full border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"></div>
          </div>
        </div>
        <div class="mt-4 flex items-center gap-3">
          <svg class="h-4 w-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
          <input type="range" [min]="minZoom" max="3" step="0.01" [(ngModel)]="zoomLevel"
            class="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-gray-200 accent-rose-600 dark:bg-gray-700" />
          <svg class="h-5 w-5 shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
        </div>
        <p class="mt-1 text-center text-xs text-gray-400">Drag to reposition · Slide to zoom</p>
        <div class="mt-5 flex flex-col items-center gap-2">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Preview</p>
          <div
            class="relative h-20 w-20 overflow-hidden rounded-full border-2 border-gray-200 shadow dark:border-gray-700"
            style="flex-shrink:0;">
            <img [src]="selectedImageUrl"
              [style.transform]="'translate(calc(-50% + ' + previewOffsetX + 'px), calc(-50% + ' + previewOffsetY + 'px)) scale(' + zoomLevel + ')'"
              [style.width]="previewImgSize" [style.height]="'auto'"
              style="max-width:none; position:absolute; top:50%; left:50%; transform-origin:center;" />
          </div>
        </div>
      </div>

    </div>

    <div class="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-white dark:bg-gray-900">
      <div class="flex items-center gap-3 justify-end">
        <app-button size="sm" variant="outline" (btnClick)="closeProfileImageModal()">Close</app-button>
        <app-button size="sm" [disabled]="!selectedImageUrl || isUploading" (btnClick)="uploadImage()">
          <span *ngIf="!isUploading">Upload</span>
          <span *ngIf="isUploading" class="flex items-center gap-2">
            <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            Uploading...
          </span>
        </app-button>
      </div>
    </div>
    <canvas #exportCanvas class="hidden"></canvas>
  </div>
</app-modal>


<!-- ═══════════════════════════════════════════════════════════
     COVER IMAGE MODAL  (file size updated to 20MB; no camera)
════════════════════════════════════════════════════════════ -->
<app-modal [isOpen]="isCoverImageOpen" (close)="closeCoverImageModal()" className="max-w-[700px]">
  <div class="relative w-full h-full flex flex-col bg-white dark:bg-gray-900 rounded-3xl overflow-hidden">

    <div class="flex-shrink-0 px-6 pt-6 pb-4 bg-white dark:bg-gray-900 border-b border-gray-200">
      <h4 class="text-2xl font-semibold text-gray-800 dark:text-white/90">Cover Image</h4>
    </div>

    <div class="flex-1 overflow-y-auto px-6 py-5">
      <div *ngIf="!selectedCoverUrl">
        <div
          class="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 p-10 text-center transition-colors dark:border-gray-700 dark:bg-gray-800"
          [class.border-rose-500]="isCoverDragging" [class.bg-rose-50]="isCoverDragging"
          (dragover)="onCoverDragOver($event)" (dragleave)="onCoverDragLeave($event)" (drop)="onCoverDrop($event)">
          <div class="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
            <svg class="h-7 w-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Select a file or drag and drop here</p>
            <p class="mt-1 text-xs text-gray-400">JPG, PNG, JPEG, HEIC — max size 20MB</p>
          </div>
          <button type="button" (click)="coverFileInput.click()"
            class="mt-2 rounded-lg bg-rose-600 px-5 py-2 text-sm font-semibold uppercase tracking-wider text-white transition hover:bg-rose-700 active:scale-95">
            SELECT FILE
          </button>
          <input #coverFileInput type="file" accept="image/jpg,image/jpeg,image/png,.jfif,image/heic,image/heif,.heic,.heif" class="hidden"
            (change)="onCoverFileSelected($event)" />
        </div>
        <p *ngIf="coverFileError" class="mt-2 text-center text-sm text-red-500">{{ coverFileError }}</p>
      </div>

      <div *ngIf="selectedCoverUrl">
        <div class="mb-3 flex items-center justify-between">
          <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Adjust &amp; Crop</span>
          <button type="button" (click)="resetCoverImage()" class="text-xs text-rose-500 hover:underline">
            Remove &amp; choose again
          </button>
        </div>
        <div class="relative flex items-center justify-center overflow-hidden rounded-2xl bg-gray-100 dark:bg-gray-800"
          style="height: 280px;">
          <img #coverCropImage [src]="selectedCoverUrl"
            [style.transform]="'translate(' + coverOffsetX + 'px,' + coverOffsetY + 'px) scale(' + coverZoomLevel + ')'"
            style="max-width: 50%; cursor: grab; transition: transform 0.05s linear; user-select: none;"
            draggable="false" (mousedown)="startCoverDrag($event)" (touchstart)="startCoverDragTouch($event)" />
          <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div
              style="width:338px; height:160px; border:2px solid white; border-radius:8px; box-shadow:0 0 0 9999px rgba(0,0,0,0.45);">
            </div>
          </div>
        </div>
        <div class="mt-4 flex items-center gap-3">
          <svg class="h-4 w-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
          <input type="range" [min]="coverMinZoom" max="3" step="0.01" [(ngModel)]="coverZoomLevel"
            class="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-gray-200 accent-rose-600 dark:bg-gray-700" />
          <svg class="h-5 w-5 shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
        </div>
        <p class="mt-1 text-center text-xs text-gray-400">Drag to reposition · Slide to zoom</p>
        <div class="mt-5 flex flex-col items-center gap-2">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Preview</p>
          <div class="relative overflow-hidden rounded-lg border-2 border-gray-200 shadow dark:border-gray-700"
            style="width:169px; height:80px; flex-shrink:0;">
            <img [src]="selectedCoverUrl"
              [style.transform]="'translate(calc(-50% + ' + coverPreviewOffsetX + 'px), calc(-50% + ' + coverPreviewOffsetY + 'px)) scale(' + coverZoomLevel + ')'"
              [style.width]="coverPreviewImgW" [style.height]="'auto'"
              style="max-width:none; position:absolute; top:50%; left:50%; transform-origin:center;" />
          </div>
        </div>
      </div>
    </div>

    <div class="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-white dark:bg-gray-900">
      <div class="flex items-center gap-3 justify-end">
        <app-button size="sm" variant="outline" (btnClick)="closeCoverImageModal()">Close</app-button>
        <app-button size="sm" [disabled]="!selectedCoverUrl || isCoverUploading" (btnClick)="uploadCoverImage()">
          <span *ngIf="!isCoverUploading">Upload</span>
          <span *ngIf="isCoverUploading" class="flex items-center gap-2">
            <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            Uploading...
          </span>
        </app-button>
      </div>
    </div>
    <canvas #coverExportCanvas class="hidden"></canvas>
  </div>
</app-modal>

<app-friend-request-limit-popup [isOpen]="showFriendLimitPopup" [hours]="friendLimitHours"
  (closed)="showFriendLimitPopup = false"></app-friend-request-limit-popup>

<app-confirmation-popup [isOpen]="showUnfriendConfirm" icon="warning" title="Unfriend User"
  message="Are you sure you want to unfriend this user?" confirmLabel="Unfriend" cancelLabel="Cancel"
  (confirmed)="confirmUnfriend()" (cancelled)="cancelUnfriendConfirm()"></app-confirmation-popup>

<app-block-user-confirm [isOpen]="showBlockConfirm" [targetName]="userInfo?.userName || ''"
  (confirmed)="confirmBlockUser()" (cancelled)="cancelBlockConfirm()"></app-block-user-confirm>
